import * as fs from 'fs';
import * as path from 'path';

type FindingSeverity = 'error' | 'warning' | 'info';

interface ValidationFinding {
  id: string;
  severity: FindingSeverity;
  message: string;
  evidence?: string[];
  suggestion?: string;
}

interface MeasureBlock {
  name?: string;
  label?: string;
  type?: string;
}

interface MeasureNode {
  n?: string;
  t?: string;
  f?: [number, number, number, number];
  vf?: [number, number, number, number];
  bg?: string;
  gd?: string;
  sh?: string[];
  bd?: string;
  token?: string;
  r?: number;
  svgFile?: string;
  svgMode?: string;
  iconSketchName?: string;
  bitmapFile?: string;
  bitmapPath?: string;
  bitmapScales?: string[];
  bitmapLookupCandidates?: string[];
  bitmapReuseMode?: string;
  bitmapReuseKey?: string;
  image?: boolean;
  cs?: [number, number];
  xs?: [number, number];
  ch?: MeasureNode[];
}

interface MeasurePayload {
  artboard?: { name?: string; w?: number; h?: number };
  designSurface?: {
    width?: number;
    height?: number;
    maxWidth?: number;
    wideScreenRule?: string;
    wideScreenHint?: string | null;
  };
  restorationContract?: {
    pageTitle?: string;
    blockOrder?: MeasureBlock[];
    navCopyLedger?: string[];
    bottomNavigation?: { present?: boolean };
    hardRules?: string[];
  };
  tree?: MeasureNode;
}

interface AssetContract {
  sketchName: string;
  fileName?: string;
  mode?: string;
  sourceType: 'vector' | 'bitmap';
  scaleVariants?: string[];
  lookupCandidates?: string[];
  reuseMode?: string;
  reuseKey?: string;
  containerSize?: [number, number];
  contentSize?: [number, number];
}

interface VisualFrameContract {
  nodeName: string;
  frame: [number, number, number, number];
  visualFrame: [number, number, number, number];
}

export interface ValidateRestorationArgs {
  measurePath?: string;
  measureContent?: string;
  codeFilePath?: string;
  routeFilePath?: string;
  routeName?: string;
  projectPath?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseNestedJson(raw: string): unknown {
  const queue: string[] = [raw.trim()];
  const seen = new Set<string>();
  let lastError: unknown = null;

  while (queue.length > 0) {
    const candidate = queue.shift()?.trim();
    if (!candidate || seen.has(candidate)) {
      continue;
    }
    seen.add(candidate);

    try {
      const parsed = JSON.parse(candidate);
      if (typeof parsed === 'string') {
        queue.push(parsed.trim());
        continue;
      }

      if (isRecord(parsed) && Array.isArray(parsed.content) && parsed.content[0] && isRecord(parsed.content[0])) {
        const text = parsed.content[0].text;
        if (typeof text === 'string') {
          queue.push(text.trim());
          continue;
        }
      }

      return parsed;
    } catch (error) {
      lastError = error;
    }

    if ((candidate.startsWith("'") && candidate.endsWith("'")) || (candidate.startsWith('"') && candidate.endsWith('"'))) {
      queue.push(candidate.slice(1, -1));
    }

    const decoded = candidate
      .replace(/\\r/g, '\r')
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t')
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, '\\');
    if (decoded !== candidate) {
      queue.push(decoded);
    }

    const firstBrace = candidate.indexOf('{');
    const lastBrace = candidate.lastIndexOf('}');
    if (firstBrace !== -1 && lastBrace > firstBrace) {
      const sliced = candidate.slice(firstBrace, lastBrace + 1);
      if (sliced !== candidate) {
        queue.push(sliced);
      }

      const decodedSliced = sliced
        .replace(/\\r/g, '\r')
        .replace(/\\n/g, '\n')
        .replace(/\\t/g, '\t')
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\');
      if (decodedSliced !== sliced) {
        queue.push(decodedSliced);
      }
    }
  }

  if (lastError instanceof Error) {
    throw lastError;
  }
  throw new Error('measure payload 解析失败');
}

function loadMeasurePayload(args: ValidateRestorationArgs): MeasurePayload {
  const raw = args.measureContent || (args.measurePath ? fs.readFileSync(args.measurePath, 'utf-8') : '');

  if (!raw) {
    throw new Error('validate_restoration 需要 measureContent 或 measurePath');
  }

  const parsed = parseNestedJson(raw);
  if (!isRecord(parsed)) {
    throw new Error('measure payload 解析失败，结果不是对象');
  }

  return parsed as MeasurePayload;
}

function normalizeText(value: string): string {
  return value.replace(/\s+/g, '').toLowerCase();
}

function normalizeSemanticKey(value: string): string {
  return value.replace(/[\s_-]+/g, '').toLowerCase();
}

function toPosixAssetPath(value: string): string {
  return value.replace(/\\/g, '/').replace(/[?#].*$/, '');
}

function getAssetBaseName(value: string): string {
  return path.posix.basename(toPosixAssetPath(value));
}

function normalizeAssetMatchKey(value: string): string {
  return getAssetBaseName(value)
    .replace(/\.[^.]+$/, '')
    .replace(/@\d+(?:\.\d+)?x$/i, '')
    .replace(/[\s_-]+/g, '')
    .toLowerCase();
}

function hasAnyPattern(source: string, patterns: RegExp[]): boolean {
  return patterns.some((pattern) => pattern.test(source));
}

function collectAssetContracts(node: MeasureNode | undefined, bucket: AssetContract[] = []): AssetContract[] {
  if (!node) {
    return bucket;
  }

  if (typeof node.svgFile === 'string') {
    bucket.push({
      sketchName: node.iconSketchName || node.n || node.svgFile,
      fileName: getAssetBaseName(node.svgFile),
      mode: node.svgMode,
      sourceType: 'vector',
      containerSize: node.cs,
      contentSize: node.xs,
    });
  }

  if (node.t === 'Image') {
    bucket.push({
      sketchName: node.n || 'Image',
      fileName: typeof node.bitmapFile === 'string'
        ? getAssetBaseName(node.bitmapFile)
        : (typeof node.bitmapPath === 'string' ? getAssetBaseName(node.bitmapPath) : undefined),
      sourceType: 'bitmap',
      scaleVariants: node.bitmapScales,
      lookupCandidates: node.bitmapLookupCandidates,
      reuseMode: node.bitmapReuseMode,
      reuseKey: node.bitmapReuseKey,
    });
  }

  for (const child of node.ch || []) {
    collectAssetContracts(child, bucket);
  }

  return bucket;
}

function extractCodeAssets(codeContent: string): string[] {
  const matches = [...codeContent.matchAll(/['"`]([^'"`\n]+\.(?:svg|png|jpg|jpeg|webp))(?:\?[^'"`\n]*)?['"`]/gi)]
    .map(([, assetPath]) => toPosixAssetPath(assetPath.trim()))
    .filter((assetPath) => !/^(https?:\/\/|data:|<svg)/i.test(assetPath));

  return [...new Set(matches)];
}

function hasExplicitBitmapScaleSuffix(assetPath: string): boolean {
  return /@\d+(?:\.\d+)?x\.(png|jpg|jpeg|webp)$/i.test(getAssetBaseName(assetPath));
}

function isCriticalBitmapEntryName(name: string): boolean {
  return /help[_ -]?button|customer[_ -]?service(?:[_ -]?(button|icon))?|support(?:[_ -]?entry)?|帮助|客服/i.test(name);
}

function buildBitmapCandidateSet(contract: AssetContract): Set<string> {
  const candidates = new Set<string>();
  if (contract.fileName) {
    candidates.add(getAssetBaseName(contract.fileName).toLowerCase());
  }
  for (const candidate of contract.lookupCandidates || []) {
    candidates.add(getAssetBaseName(candidate).toLowerCase());
  }
  return candidates;
}

function buildBitmapMatchKeySet(contract: AssetContract): Set<string> {
  const matchKeys = new Set<string>();
  if (contract.fileName) {
    matchKeys.add(normalizeAssetMatchKey(contract.fileName));
  }
  for (const candidate of contract.lookupCandidates || []) {
    matchKeys.add(normalizeAssetMatchKey(candidate));
  }
  return matchKeys;
}

function collectCriticalBitmapContracts(payload: MeasurePayload, assetContracts: AssetContract[]): AssetContract[] {
  const criticalBitmapKeys = new Set(
    (payload.restorationContract?.blockOrder || [])
      .flatMap((block) => {
        const blockIdentity = `${block.name || ''} ${block.label || ''}`;
        if (block.type !== 'Image' || !isCriticalBitmapEntryName(blockIdentity)) {
          return [];
        }

        return [block.name, block.label]
          .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
          .map((value) => normalizeSemanticKey(value));
      }),
  );

  if (criticalBitmapKeys.size === 0) {
    return [];
  }

  return assetContracts.filter((contract) =>
    contract.sourceType === 'bitmap'
    && criticalBitmapKeys.has(normalizeSemanticKey(contract.sketchName)),
  );
}

function hasCodePropertyLiteral(codeContent: string, property: string, value: number): boolean {
  const escapedValue = String(value).replace('.', '\\.');
  return new RegExp(`${property}\\s*:\\s*${escapedValue}(?:\\.0+)?\\b`).test(codeContent);
}

function collectPatternEvidence(
  codeContent: string,
  patterns: Array<{ pattern: RegExp; label: string }>,
): string[] {
  return patterns
    .filter(({ pattern }) => pattern.test(codeContent))
    .map(({ label }) => label);
}

function detectSystemStatusBarRenderEvidence(codeContent: string): string[] {
  return collectPatternEvidence(codeContent, [
    { pattern: /['\"]9:41['\"]/, label: '9:41' },
    { pattern: /signal\.svg/i, label: 'signal.svg' },
    { pattern: /wifi\.svg/i, label: 'wifi.svg' },
    { pattern: /battery\.svg/i, label: 'battery.svg' },
    { pattern: /\b_StatusBar[A-Za-z0-9_]*\b/, label: '_StatusBar*' },
    { pattern: /\bStatusBar[A-Za-z0-9_]*\b/, label: 'StatusBar*' },
    { pattern: /\bstatusBar[A-Za-z0-9_]*\b/, label: 'statusBar*' },
  ]);
}

function detectFullPageArtboardClampEvidence(codeContent: string, designWidth: number): string[] {
  const escapedWidth = String(designWidth).replace('.', '\\.')
  const evidence = collectPatternEvidence(codeContent, [
    { pattern: new RegExp(`_designSurfaceWidth\\s*=\\s*${escapedWidth}(?:\\.0+)?\\b`), label: `_designSurfaceWidth = ${designWidth}` },
    { pattern: new RegExp(`maxWidth\\s*:\\s*${escapedWidth}(?:\\.0+)?\\b`), label: `maxWidth: ${designWidth}` },
    { pattern: new RegExp(`width\\s*:\\s*${escapedWidth}(?:\\.0+)?\\b`), label: `width: ${designWidth}` },
    { pattern: /ConstrainedBox\s*\(/, label: 'ConstrainedBox' },
    { pattern: /BoxConstraints\s*\([^)]*maxWidth\s*:/, label: 'BoxConstraints(maxWidth: ...)' },
    { pattern: /LayoutBuilder\s*\(/, label: 'LayoutBuilder' },
    { pattern: /math\.(min|max)\(/, label: 'math.min/math.max' },
    { pattern: /constraints\.maxWidth/, label: 'constraints.maxWidth' },
    { pattern: /Align\s*\(/, label: 'Align' },
    { pattern: /Center\s*\(/, label: 'Center' },
    { pattern: /SizedBox\s*\([^)]*width\s*:/, label: 'SizedBox(width: ...)' },
  ]);

  const hasExplicitClampWidth = evidence.some((item) => item.startsWith('_designSurfaceWidth') || item.startsWith('maxWidth:') || item.startsWith('width:'));
  const hasResponsiveClamp = evidence.includes('LayoutBuilder')
    && evidence.includes('math.min/math.max')
    && evidence.includes('constraints.maxWidth');
  const hasCenteredShell = hasExplicitClampWidth
    && evidence.some((item) => item === 'ConstrainedBox' || item === 'BoxConstraints(maxWidth: ...)' || item === 'Align' || item === 'Center' || item === 'SizedBox(width: ...)');

  return hasResponsiveClamp || hasCenteredShell ? evidence : [];
}

function collectVisualFrameContracts(node: MeasureNode | undefined, bucket: VisualFrameContract[] = []): VisualFrameContract[] {
  if (!node) {
    return bucket;
  }

  if (node.f && node.vf) {
    const hasVisualDelta = node.f.some((value, index) => value !== node.vf![index]);
    const hasVisualDecoration = Boolean(
      node.bg
      || node.gd
      || node.bd
      || node.token
      || node.sh?.length
      || node.r !== undefined,
    );
    if (hasVisualDelta && hasVisualDecoration) {
      bucket.push({
        nodeName: node.n || node.t || 'unknown',
        frame: node.f,
        visualFrame: node.vf,
      });
    }
  }

  for (const child of node.ch || []) {
    collectVisualFrameContracts(child, bucket);
  }

  return bucket;
}

function extractRouteChunk(routeContent: string, routeIndex: number): string {
  const blockStart = routeContent.lastIndexOf('GetPage(', routeIndex);
  const start = blockStart === -1 ? routeIndex : blockStart;
  const nextBlockStart = routeContent.indexOf('GetPage(', routeIndex + 1);

  if (nextBlockStart === -1) {
    return routeContent.slice(start);
  }

  return routeContent.slice(start, nextBlockStart);
}

function resolveProjectAssetPath(projectPath: string, assetPath: string): string {
  const normalizedPath = toPosixAssetPath(assetPath)
    .replace(/^\.\//, '')
    .replace(/^@\//, 'src/');

  return path.join(projectPath, normalizedPath.replace(/^\/+/, ''));
}

function getBitmapScaleVariantPaths(projectPath: string, assetPath: string): string[] {
  const relativeDir = path.posix.dirname(assetPath);
  const fileName = path.posix.basename(assetPath);

  return [
    path.join(projectPath, relativeDir, '2.0x', fileName),
    path.join(projectPath, relativeDir, '3.0x', fileName),
  ];
}

function extractPageClassName(codeContent: string): string | null {
  const match = codeContent.match(/class\s+([A-Za-z0-9_]+)\s+extends\s+(StatelessWidget|StatefulWidget)/);
  return match?.[1] || null;
}

function collectContractTexts(payload: MeasurePayload): string[] {
  const blockTexts = (payload.restorationContract?.blockOrder || [])
    .flatMap((block) => [block.name, block.label])
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

  const navTexts = (payload.restorationContract?.navCopyLedger || [])
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

  const pageTitle = payload.restorationContract?.pageTitle;

  return [...new Set([
    ...blockTexts,
    ...navTexts,
    ...(pageTitle ? [pageTitle] : []),
  ])];
}

function buildInteractionValidator(payload: MeasurePayload, codeContent: string) {
  const findings: ValidationFinding[] = [];
  const contractTexts = collectContractTexts(payload).join(' ');
  const combinedText = `${contractTexts}\n${codeContent}`;

  const hasSearchSemantics = /搜索/.test(combinedText);
  const hasRealTextInput = hasAnyPattern(codeContent, [
    /TextField\s*\(/,
    /TextFormField\s*\(/,
    /SearchBar\s*\(/,
    /CupertinoSearchTextField\s*\(/,
  ]);

  if (hasSearchSemantics && !hasRealTextInput) {
    findings.push({
      id: 'search_control_static_shell',
      severity: 'error',
      message: '设计/代码中存在搜索语义，但未检测到真实输入控件，当前实现很可能只是静态搜索框壳子。',
      suggestion: '至少补齐 TextField/SearchBar，让搜索框可聚焦可输入；无后端时优先做本地过滤。',
    });
  }

  const hasSelectorPlaceholder = /请选择/.test(combinedText);
  const hasSelectionMechanism = hasAnyPattern(codeContent, [
    /showModalBottomSheet\s*\(/,
    /DropdownButton(?:FormField)?(?:<[^>]+>)?\s*\(/,
    /PopupMenuButton(?:<[^>]+>)?\s*\(/,
    /showMenu\s*\(/,
    /showCupertinoModalPopup\s*\(/,
    /MenuAnchor\s*\(/,
    /CupertinoPicker\s*\(/,
  ]);

  if (hasSelectorPlaceholder && !hasSelectionMechanism) {
    findings.push({
      id: 'selector_control_static_shell',
      severity: 'error',
      message: '代码中存在“请选择”类表单语义，但未检测到任何真实选择机制，当前实现很可能只是静态选择器壳子。',
      suggestion: '至少补齐基础 bottom sheet / dropdown / popup menu，并提供少量假数据占位，不要把选择器留成纯展示状态。',
    });
  }

  return {
    passed: !findings.some((item) => item.severity === 'error'),
    findings,
  };
}

function buildContractValidator(payload: MeasurePayload, codeContent: string) {
  const findings: ValidationFinding[] = [];
  const blockOrder = payload.restorationContract?.blockOrder || [];
  const navCopyLedger = payload.restorationContract?.navCopyLedger || [];
  const pageTitle = payload.restorationContract?.pageTitle;

  let requiredBlocks = 0;
  let matchedBlocks = 0;

  if (pageTitle) {
    requiredBlocks += 1;
    if (codeContent.includes(pageTitle)) {
      matchedBlocks += 1;
    } else {
      findings.push({
        id: 'missing_page_title',
        severity: 'error',
        message: `代码中未找到设计标题“${pageTitle}”`,
        suggestion: '先按 restorationContract.pageTitle 对齐页面标题，再继续修局部样式。',
      });
    }
  }

  const needsBackButton = blockOrder.some((block) => /back/i.test(`${block.name || ''} ${block.label || ''}`));
  if (needsBackButton) {
    requiredBlocks += 1;
    if (hasAnyPattern(codeContent, [/Get\.back\b/, /Navigator\.pop\b/, /arrow_back/i, /back_icon\.svg/, /back_button/i])) {
      matchedBlocks += 1;
    } else {
      findings.push({
        id: 'missing_back_button_block',
        severity: 'error',
        message: '设计稿存在 Back Button，但代码中未检测到返回按钮实现。',
        suggestion: '补齐返回按钮交互和视觉实现，不要只保留标题。',
      });
    }
  }

  const needsSupportButton = blockOrder.some((block) => /customer service|support|客服|help[_ -]?button|帮助/i.test(`${block.name || ''} ${block.label || ''}`));
  if (needsSupportButton) {
    requiredBlocks += 1;
    if (hasAnyPattern(codeContent, [/customer[_ -]?service/i, /support/i, /help[_ -]?button/i, /recipient_customer_service_button/i, /客服/])) {
      matchedBlocks += 1;
    } else {
      findings.push({
        id: 'missing_customer_service_block',
        severity: 'error',
        message: '设计稿存在 Help/Customer Service Button，但代码中未检测到对应入口。',
        suggestion: '补齐右上角帮助/客服入口，并保持与设计图层语义一致，不要把 Help Button 替换成其他客服图标。',
      });
    }
  }

  const optionLabels = ['支付宝', '微信', '银行卡'].filter((label) => navCopyLedger.includes(label));
  if (optionLabels.length > 0) {
    requiredBlocks += 1;
    const missingOptions = optionLabels.filter((label) => !codeContent.includes(label));
    if (missingOptions.length === 0) {
      matchedBlocks += 1;
    } else {
      findings.push({
        id: 'missing_option_labels',
        severity: 'error',
        message: `代码中缺少设计稿选项文案: ${missingOptions.join(' / ')}`,
        suggestion: '选项卡文案默认以 navCopyLedger 为真值，禁止自行改写或遗漏。',
      });
    }
  }

  const systemStatusBarEvidence = detectSystemStatusBarRenderEvidence(codeContent);
  if (systemStatusBarEvidence.length > 0) {
    findings.push({
      id: 'system_status_bar_rendered',
      severity: 'error',
      message: '代码中直接渲染了 Sketch 顶部时间/信号/WiFi/电池等设备系统 chrome；这些元素不应被画进 Flutter 页面。',
      evidence: systemStatusBarEvidence,
      suggestion: '改用 Scaffold + SafeArea/AppBar 处理顶部安全区域，不要把 9:41 / signal / wifi / battery 还原为业务 UI。',
    });
  }

  const designWidth = payload.designSurface?.maxWidth || payload.artboard?.w;
  if (designWidth && designWidth <= 430) {
    const clampEvidence = detectFullPageArtboardClampEvidence(codeContent, designWidth);
    if (clampEvidence.length > 0) {
      findings.push({
        id: 'full_page_artboard_clamp_detected',
        severity: 'error',
        message: `当前页面看起来把整页锁成了 ${designWidth} 宽 design surface/artboard 壳子；全屏 app 页面在宽屏应跟随宿主布局与页面 padding 适配，不应整页 clamp。`,
        evidence: clampEvidence,
        suggestion: '改用 Scaffold + SafeArea + ListView/AppBar + 页面 padding 的真实页面骨架；只有明确测得的局部模态或浮层容器才做 clamp。',
      });
    }
  }

  const visualFrameContracts = collectVisualFrameContracts(payload.tree);
  for (const contract of visualFrameContracts) {
    const [, , frameWidth, frameHeight] = contract.frame;
    const [, , visualWidth, visualHeight] = contract.visualFrame;
    const widthMismatch = frameWidth !== visualWidth
      && hasCodePropertyLiteral(codeContent, 'width', frameWidth)
      && !hasCodePropertyLiteral(codeContent, 'width', visualWidth);
    const heightMismatch = frameHeight !== visualHeight
      && hasCodePropertyLiteral(codeContent, 'height', frameHeight)
      && !hasCodePropertyLiteral(codeContent, 'height', visualHeight);

    if (widthMismatch || heightMismatch) {
      findings.push({
        id: 'visual_frame_literal_mismatch',
        severity: 'warning',
        message: `${contract.nodeName} 的视觉边界是 ${visualWidth}x${visualHeight}，但代码看起来仍在使用逻辑 frame ${frameWidth}x${frameHeight}，容易造成底边距或阴影视觉误差。`,
        evidence: [`${contract.nodeName}: frame=${contract.frame.join(',')} visualFrame=${contract.visualFrame.join(',')}`],
        suggestion: '当 compact 同时给出 f 与 vf 时，外层容器尺寸和由此推导的底部视觉间距优先以 vf 为准。',
      });
    }
  }

  return {
    passed: !findings.some((item) => item.severity === 'error'),
    requiredBlocks,
    matchedBlocks,
    findings,
  };
}

function buildAssetValidator(payload: MeasurePayload, codeContent: string, args: ValidateRestorationArgs) {
  const findings: ValidationFinding[] = [];
  const assetContracts = collectAssetContracts(payload.tree);
  const criticalBitmapContracts = collectCriticalBitmapContracts(payload, assetContracts);
  const expectedVectorFiles = new Set(
    assetContracts
      .filter((item) => item.sourceType === 'vector' && item.fileName)
      .map((item) => item.fileName!.toLowerCase()),
  );
  const expectedBitmapCount = assetContracts.filter((item) => item.sourceType === 'bitmap').length;
  const codeAssets = extractCodeAssets(codeContent);
  const codeVectorAssets = codeAssets.filter((asset) => asset.toLowerCase().endsWith('.svg'));
  const codeBitmapAssets = codeAssets.filter((asset) => /\.(png|jpg|jpeg|webp)$/i.test(asset));
  const codeBitmapFileNames = new Set(
    codeBitmapAssets.map((asset) => getAssetBaseName(asset).toLowerCase()),
  );
  const codeBitmapMatchKeys = new Set(
    codeBitmapAssets.map((asset) => normalizeAssetMatchKey(asset)),
  );

  const unexpectedVectorAssets = codeVectorAssets.filter((asset) => !expectedVectorFiles.has(getAssetBaseName(asset).toLowerCase()));
  if (unexpectedVectorAssets.length > 0) {
    findings.push({
      id: 'unexpected_vector_assets',
      severity: 'error',
      message: '代码中存在未在当前设计稿 contract 中出现的 SVG 资源，疑似复用了旧页面资产。',
      evidence: unexpectedVectorAssets,
      suggestion: '对这些图标重新从当前画板局部导出，或在 validate_restoration 通过前不要继续提交页面。',
    });
  }

  if (expectedBitmapCount > 0 && codeBitmapAssets.length === 0) {
    findings.push({
      id: 'missing_bitmap_assets',
      severity: criticalBitmapContracts.length > 0 ? 'error' : 'warning',
      message: criticalBitmapContracts.length > 0
        ? `当前设计稿包含 ${expectedBitmapCount} 个 Image 图层，且关键入口位图不能被语义相近的 SVG/自绘实现替代，但代码里没有任何位图资源引用。`
        : `当前设计稿包含 ${expectedBitmapCount} 个 Image 图层，但代码里没有任何位图资源引用。`,
      suggestion: criticalBitmapContracts.length > 0
        ? '关键入口类 Image block 必须先按设计图层名和 bitmapLookupCandidates 复用真实位图；不要再把 Help / Customer Service / Support Entry 画成自定义 SVG 或壳子组件。'
        : '检查是否把 Sketch Image 图层错误当成 SVG 或代码绘制了。',
    });
  }

  if (criticalBitmapContracts.length > 0 && codeBitmapAssets.length > 0) {
    const mismatchedCriticalBitmapEntries = criticalBitmapContracts
      .map((contract) => {
        const candidateSet = buildBitmapCandidateSet(contract);
        const candidateMatchKeys = buildBitmapMatchKeySet(contract);
        const matched = [...codeBitmapMatchKeys].some((matchKey) => candidateMatchKeys.has(matchKey));
        return matched
          ? null
          : `${contract.sketchName} -> expected asset stem matching one of ${[...candidateSet].join(', ')}`;
      })
      .filter((item): item is string => item !== null);

    if (mismatchedCriticalBitmapEntries.length > 0) {
      findings.push({
        id: 'critical_bitmap_entry_not_reused',
        severity: 'error',
        message: '关键 Image 入口没有复用设计源位图，存在把 Help / Customer Service 等语义相近图标互换的风险。',
        evidence: mismatchedCriticalBitmapEntries,
        suggestion: '先对照 restorationContract.blockOrder 中的真实块名，再按 bitmapLookupCandidates 或 canonical 文件名复用位图；未命中时应停止猜测并要求重新确认设计导出。',
      });
    }
  }

  if (codeBitmapAssets.length > 0) {
    findings.push({
      id: 'bitmap_asset_source_check_required',
      severity: 'info',
      message: '代码中存在位图资源，需人工确认它们来自当前画板，或已正确复用当前设计约定的 canonical 共享资产。',
      evidence: codeBitmapAssets,
      suggestion: '优先按设计稿图层名主体与 bitmapLookupCandidates 在项目现有素材目录中查找资源；文件名匹配重点看主体，例如 xxx / xxx@4x，只有缺失时才按 contract 指定方式补导或复用共享资产。',
    });
  }

  const sharedBitmapFiles = assetContracts
    .filter((item) => item.sourceType === 'bitmap' && item.reuseMode === 'shared-canonical' && item.fileName)
    .map((item) => ({
      fileName: item.fileName!,
      matchKey: normalizeAssetMatchKey(item.fileName!),
    }));

  if (sharedBitmapFiles.length > 0) {
    const missingSharedBitmapFiles = sharedBitmapFiles
      .filter((item) => !codeBitmapMatchKeys.has(item.matchKey))
      .map((item) => item.fileName);

    if (missingSharedBitmapFiles.length > 0) {
      findings.push({
        id: 'shared_bitmap_asset_not_reused',
        severity: 'warning',
        message: '设计稿已标记该位图为共享 canonical 资产，但代码没有复用对应文件名主体，存在重复缓存或把 Help/Customer Service 语义误合并的风险。',
        evidence: missingSharedBitmapFiles,
        suggestion: '优先复用 contract 返回的共享 bitmap 文件名主体；目录和 @4x 只是项目实现细节，Help Button 与 Customer Service Button 必须按各自主体独立复用，不能再合并成同一个名字。',
      });
    }
  }

  const requiresPageLocalBitmap = assetContracts.some(
    (item) => item.sourceType === 'bitmap' && item.reuseMode !== 'shared-canonical',
  );

  if (expectedBitmapCount > 0 && codeBitmapAssets.length > 0 && requiresPageLocalBitmap) {
    const hasScaledBitmapExport = codeBitmapAssets.some((asset) => hasExplicitBitmapScaleSuffix(asset));
    if (!hasScaledBitmapExport) {
      const pageClassName = extractPageClassName(codeContent) || '';
      const normalizedStem = pageClassName
        .replace(/Page$/, '')
        .replace(/([a-z])([A-Z])/g, '$1_$2')
        .toLowerCase();

      if (normalizedStem) {
        const hasPageLocalBitmap = codeBitmapAssets.some((asset) =>
          getAssetBaseName(asset).toLowerCase().includes(normalizedStem),
        );

        if (!hasPageLocalBitmap) {
          findings.push({
            id: 'bitmap_assets_not_page_local',
            severity: 'warning',
            message: '当前页面位图资源看起来不是 page-local 命名，存在继续复用旧页面 PNG 的风险。',
            evidence: codeBitmapAssets,
            suggestion: '位图资源优先按当前页面命名导出，例如带上页面 stem；若项目已采用显式倍率文件名（如 xxx@4x.png），则无需再追加 page-local stem。',
          });
        }
      }
    }
  }

  const expectedBitmapScales = new Set(
    assetContracts
      .filter((item) => item.sourceType === 'bitmap')
      .flatMap((item) => item.scaleVariants || []),
  );

  if (expectedBitmapCount > 0 && codeBitmapAssets.length > 0 && args.projectPath) {
    const requiresRetinaVariants = expectedBitmapScales.size === 0 || expectedBitmapScales.has('2.0x') || expectedBitmapScales.has('3.0x');

    if (requiresRetinaVariants) {
      const missingRetinaEvidence: string[] = [];

      for (const asset of codeBitmapAssets) {
        if (hasExplicitBitmapScaleSuffix(asset)) {
          continue;
        }

        const assetOnDisk = resolveProjectAssetPath(args.projectPath, asset);
        if (!fs.existsSync(assetOnDisk)) {
          missingRetinaEvidence.push(`${asset} (base missing)`);
          continue;
        }

        const variantPaths = getBitmapScaleVariantPaths(args.projectPath, asset);
        const missingVariants = variantPaths
          .filter((variantPath) => !fs.existsSync(variantPath))
          .map((variantPath) => path.relative(args.projectPath!, variantPath));

        if (missingVariants.length > 0) {
          missingRetinaEvidence.push(`${asset} -> missing ${missingVariants.join(', ')}`);
        }
      }

      if (missingRetinaEvidence.length > 0) {
        findings.push({
          id: 'bitmap_retina_variants_missing',
          severity: 'error',
          message: '位图资源既没有使用显式倍率命名（例如 xxx@4x.png），也缺少 2.0x/3.0x 变体，高分屏下容易发糊。',
          evidence: missingRetinaEvidence,
          suggestion: '优先使用项目现有的高倍率手工导出命名（如 xxx@4x.png）；如果仍走多倍率目录流程，再补齐同目录的 2.0x 与 3.0x 变体。',
        });
      }
    }
  }

  const preserveContracts = assetContracts.filter((item) => item.mode === 'preserve');
  if (preserveContracts.length > 0 && /colorFilter\s*:|ColorFilter\./.test(codeContent)) {
    findings.push({
      id: 'preserve_svg_tint_risk',
      severity: 'warning',
      message: '当前页面代码包含 ColorFilter，而 contract 中存在 preserve 模式 SVG，存在再次染色风险。',
      evidence: preserveContracts.map((item) => item.fileName || item.sketchName),
      suggestion: '检查 preserve SVG 是否被整体 tint，必要时把图标改为保留原色资源。',
    });
  }

  return {
    passed: !findings.some((item) => item.severity === 'error'),
    expectedAssetContracts: assetContracts,
    codeAssets,
    findings,
  };
}

function escapeRegex(source: string): string {
  return source.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildRouteReviewValidator(args: ValidateRestorationArgs, codeContent: string) {
  const findings: ValidationFinding[] = [];
  const pageClassName = extractPageClassName(codeContent);
  let routeConnected: boolean | null = null;
  let routeTarget: string | null = null;
  let reviewHarnessReady: boolean | null = null;

  if (args.routeFilePath && args.routeName) {
    const routeContent = fs.readFileSync(args.routeFilePath, 'utf-8');
    const routeIndex = routeContent.indexOf(`name: ${args.routeName}`);

    if (routeIndex === -1) {
      findings.push({
        id: 'route_not_found',
        severity: 'error',
        message: `在路由文件中未找到 ${args.routeName} 的定义。`,
        suggestion: '生成页面后必须确认真实路由已经接通，不能停留在孤立页面文件。',
      });
    } else {
      const routeChunk = extractRouteChunk(routeContent, routeIndex);
      routeConnected = true;

      if (/_ComingSoonPage/.test(routeChunk)) {
        routeConnected = false;
        findings.push({
          id: 'route_points_to_placeholder',
          severity: 'error',
          message: `${args.routeName} 当前仍然指向 _ComingSoonPage，占位页未替换。`,
          suggestion: '把 page factory 接到真实页面类，而不是保留占位实现。',
        });
      }

      const routeTargetMatch = routeChunk.match(/page:\s*\(\)\s*=>\s*const\s+([A-Za-z0-9_]+)/)
        || routeChunk.match(/page:\s*\(\)\s*=>\s*([A-Za-z0-9_]+)\(/);
      routeTarget = routeTargetMatch?.[1] || null;

      if (pageClassName && routeTarget && pageClassName !== routeTarget) {
        findings.push({
          id: 'route_target_mismatch',
          severity: 'warning',
          message: `路由目标类 ${routeTarget} 与页面主类 ${pageClassName} 不一致。`,
          suggestion: '确认路由接的就是当前生成页面，而不是同名旧页面。',
        });
      }
    }
  }

  if (args.projectPath) {
    const reviewHarnessPath = path.join(args.projectPath, 'test/visual_regression/ui_review_golden_test.dart');
    reviewHarnessReady = fs.existsSync(reviewHarnessPath);

    if (!reviewHarnessReady) {
      findings.push({
        id: 'ui_review_harness_missing',
        severity: 'warning',
        message: '项目内未检测到 UI review golden harness，生成后无法立即做结构级回归。',
        suggestion: '至少补上 test/visual_regression/ui_review_golden_test.dart，再让还原页进入自动 review。',
      });
    } else {
      const reviewContent = fs.readFileSync(reviewHarnessPath, 'utf-8');
      const pageStem = path.basename(args.codeFilePath || '', path.extname(args.codeFilePath || '')).replace(/_page$/, '');
      if (pageStem && !reviewContent.includes(pageStem)) {
        findings.push({
          id: 'ui_review_scenario_not_registered',
          severity: 'warning',
          message: 'UI review harness 已存在，但当前页面还没有专属场景注册。',
          evidence: [reviewHarnessPath],
          suggestion: '把当前页面加入 visual regression 场景，避免只靠肉眼回归。',
        });
      }
    }
  }

  const recommendedNextSteps = [
    '先修正所有 error 级别问题，再处理 warning',
    '修正资源来源后重新运行 validate_restoration，直到 unexpected vector assets 清零',
  ];
  if (args.routeFilePath && args.routeName) {
    recommendedNextSteps.push(`确认 ${args.routeName} 不再指向 _ComingSoonPage`);
  }
  if (args.projectPath) {
    recommendedNextSteps.push('将页面注册到 visual regression 场景后再进行 UI review');
  }

  return {
    passed: !findings.some((item) => item.severity === 'error'),
    routeConnected,
    routeTarget,
    reviewHarnessReady,
    findings,
    recommendedNextSteps,
  };
}

export async function validateRestoration(args: ValidateRestorationArgs): Promise<{
  artboardName?: string;
  ok: boolean;
  codeFilePath: string;
  contractValidator: ReturnType<typeof buildContractValidator>;
  assetValidator: ReturnType<typeof buildAssetValidator>;
  interactionValidator: ReturnType<typeof buildInteractionValidator>;
  routeReviewValidator: ReturnType<typeof buildRouteReviewValidator>;
}> {
  if (!args.codeFilePath) {
    throw new Error('validate_restoration 需要 codeFilePath 参数');
  }
  if (!fs.existsSync(args.codeFilePath)) {
    throw new Error(`代码文件不存在: ${args.codeFilePath}`);
  }

  const payload = loadMeasurePayload(args);
  const codeContent = fs.readFileSync(args.codeFilePath, 'utf-8');

  const contractValidator = buildContractValidator(payload, codeContent);
  const assetValidator = buildAssetValidator(payload, codeContent, args);
  const interactionValidator = buildInteractionValidator(payload, codeContent);
  const routeReviewValidator = buildRouteReviewValidator(args, codeContent);

  return {
    artboardName: payload.artboard?.name,
    ok: contractValidator.passed && assetValidator.passed && interactionValidator.passed && routeReviewValidator.passed,
    codeFilePath: args.codeFilePath,
    contractValidator,
    assetValidator,
    interactionValidator,
    routeReviewValidator,
  };
}